// ============================================================================
// Elegibilidade do bloco "Your purchases" e da pergunta "worth what you paid".
//
// Funções puras, sem React e sem Supabase, para o guarda de teste provar a
// matriz inteira sem montar tela. Nasceram da auditoria 02 (21/09/2026):
// `approved_users.products` é copiado do dono para a convidada pelo Family
// Share (`grant_family_access`), então "tem o produto no array" não distingue
// compra própria de acesso recebido. Em 90 dias: 43 convidadas carregavam o
// Screen Switch e 57 o Calm Mom sem ter comprado nada.
// ============================================================================

export type Ownership = 'own' | 'shared' | 'none' | 'error';

export interface ApprovedUserRow {
  products: Array<{ id: string | number }> | null;
  status: string | null;
  source_platform: string | null;
}

/** Origens que significam "recebeu de alguém", não "pagou". */
const SHARED_SOURCES = new Set(['family', 'gift']);

/**
 * Classifica o registro de `approved_users`.
 * - `error`: a consulta falhou; o chamador grava `purchases_block_error`.
 * - `none`: sem registro, sem produtos, ou registro que não está ativo
 *   (reembolso e chargeback esvaziam o array e trocam o status).
 * - `shared`: produtos copiados por Family Share ou presente.
 * - `own`: compra própria numa plataforma de venda.
 */
export function classifyOwnership(row: ApprovedUserRow | null | undefined, failed = false): Ownership {
  if (failed) return 'error';
  if (!row) return 'none';
  if (row.status !== 'active') return 'none';
  if (!row.products || row.products.length === 0) return 'none';
  return SHARED_SOURCES.has(row.source_platform ?? '') ? 'shared' : 'own';
}

/**
 * O que ELA pagou vem de `purchase_lines` (RLS por email). Uma convidada que depois
 * compra continua `family` em `approved_users` (o webhook preserva a origem), mas
 * tem linha de compra própria: para ela, a compra é dela. Em 22/09 esse caso tinha
 * zero ocorrências; a regra existe para quando tiver.
 */
export function effectiveOwnership(recordOwnership: Ownership, purchasedProductIds: ReadonlySet<string>): Ownership {
  if (recordOwnership === 'shared' && purchasedProductIds.size > 0) return 'own';
  return recordOwnership;
}

/** Chaves de sessão para os dois instrumentos não perguntarem no mesmo dia. */
export const PROMPT_SHOWN_KEY = 'purchase-value-prompt-shown';
export const SURVEY_OPEN_KEY = 'feedback-survey-open';

export function readSessionFlag(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

export function writeSessionFlag(key: string, value: string): void {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    /* storage indisponível: os dois instrumentos seguem pelo banco */
  }
}

export function hasProductId(row: ApprovedUserRow | null | undefined, productId: string): boolean {
  if (!row?.products) return false;
  return row.products.some((p) => String(p.id) === String(productId));
}

/**
 * A pergunta "So far, was this worth what you paid?" aparece uma vez, num dia de
 * calendário posterior ao do cadastro, e só para quem pagou (`own`). A unidade é a
 * mesma da régua da onda (dia de calendário), e não precisa ler `app_events`, que
 * só admin lê (`app_events_select_admin`).
 */
export function isPurchaseValuePromptEligible(params: {
  ownership: Ownership;
  accountCreatedAt: string | null | undefined;
  now: Date;
  alreadyShown: boolean;
}): boolean {
  const { ownership, accountCreatedAt, now, alreadyShown } = params;
  if (alreadyShown) return false;
  if (ownership !== 'own') return false;
  if (!accountCreatedAt) return false;
  const created = new Date(accountCreatedAt);
  if (Number.isNaN(created.getTime())) return false;
  return calendarDay(now) > calendarDay(created);
}

/**
 * Dia de calendário em UTC, como YYYY-MM-DD. A régua da onda conta dias em UTC
 * (`app_events` × `auth.users.created_at`); a pergunta usa a mesma unidade, ou o
 * "dia seguinte" da tela e o do relatório divergem perto da meia-noite
 * (auditoria 03 reproduziu em America/Cuiaba).
 */
export function calendarDay(d: Date): string {
  return d.toISOString().slice(0, 10);
}

/**
 * `?from=` dos leitores: só caminho interno, para "voltar" nunca virar redirect
 * para fora do app. Aceita `/purchases`, recusa `//x`, `http:`, `javascript:`.
 */
export function safeInternalPath(raw: string | null | undefined): string | undefined {
  if (!raw) return undefined;
  if (raw.length > 200) return undefined;
  // Auditoria 03 (22/09): `/\t/evil.example` passava, e o navegador normaliza
  // `/<tab>/` para `//`, que é origem externa; o router cai em
  // `window.location.assign`. Controles fora, e a origem conferida DEPOIS de
  // normalizar, que é como o navegador vai ler.
  if (/[\x00-\x20\x7f]/.test(raw)) return undefined;
  if (!raw.startsWith('/') || raw.startsWith('//') || raw.includes('\\')) return undefined;
  if (/^\/[^/?#]*:/.test(raw)) return undefined;
  let parsed: URL;
  try {
    parsed = new URL(raw, 'https://internal.invalid');
  } catch {
    return undefined;
  }
  if (parsed.origin !== 'https://internal.invalid') return undefined;
  return parsed.pathname + parsed.search + parsed.hash;
}

/**
 * `?chapter=` do leitor V3: índice 0-based do capítulo, ou o id (`ch-11`).
 * Devolve `undefined` quando não há parâmetro válido, e o leitor segue o
 * progresso salvo como sempre.
 */
export function parseChapterParam(
  raw: string | null | undefined,
  chapterIds: string[]
): number | undefined {
  if (!raw) return undefined;
  const byId = chapterIds.indexOf(raw);
  if (byId >= 0) return byId;
  if (!/^\d+$/.test(raw)) return undefined;
  const n = Number(raw);
  return n >= 0 && n < chapterIds.length ? n : undefined;
}
